import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center rounded-md text-sm font-semibold shadow-xs focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid",
  {
    variants: {
      variant: {
        primary:
          "gap-2 bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 focus-visible:outline-blue-600",
        "light-primary":
          "bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-600 hover:ring-sky-600",
        brand:
          "bg-brand-600 hover:bg-brand-700 focus-visible:outline-brand-600 gap-2 px-4 py-2 text-white",
        destructive:
          "bg-red-600 font-semibold text-white shadow-xs hover:bg-red-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 focus-visible:outline-solid",
        "light-destructive":
          "border border-gray-200 font-semibold text-red-500 shadow-xs hover:border-red-100 hover:bg-red-50",
        secondary:
          "rounded-[6px] border border-gray-300 bg-white px-3 py-2 shadow-[0px_1px_2px_0px_rgba(29,60,122,0.11)] hover:bg-[#F6FAFF] hover:text-gray-800",
        default:
          "font-custom mt-3 w-auto bg-white px-3 py-2 text-gray-900 ring-1 ring-gray-300 ring-inset hover:bg-gray-50 sm:mt-0",
        ghost:
          "text-gray-600 shadow-none hover:bg-gray-100 hover:text-gray-900",
        soft: "bg-blue-[#F2F7FF] text-blue-600 hover:bg-blue-100",
        "soft-secondary":
          "bg-gray-50 text-gray-600 hover:bg-blue-50 hover:text-blue-600 hover:ring-sky-600",
        navigation:
          "relative inline-flex items-center rounded-l-md bg-white px-2 py-1 text-gray-400 ring-1 ring-gray-300 ring-inset hover:bg-blue-50 hover:text-blue-600 hover:ring-sky-600 focus:z-10 disabled:opacity-50",
        back: "bg-grey-100 h-3 w-3 text-gray-400 group-hover:text-gray-600",
        icon: "bg-[#F7F7F7] text-gray-500 hover:border-[0.5px] hover:border-[#E5EBF0] hover:bg-[#F0F3F6] hover:text-gray-900",
        gray: "rounded-[6px] border border-gray-300 bg-gray-200 px-3 py-2 shadow-[0px_1px_2px_0px_rgba(29,60,122,0.11)] hover:bg-[#F6FAFF] hover:text-gray-800",
      },
      size: {
        xs: "px-2 py-1 text-xs",
        sm: "px-2 py-1 text-sm",
        md: "px-2.5 py-1.5 text-sm",
        lg: "px-3 py-2 text-sm",
        xl: "px-3.5 py-2.5 text-sm",
        square: "flex h-8 w-8 items-center justify-center text-sm",
      },
      rounded: {
        none: "rounded-none",
        sm: "rounded-sm",
        md: "rounded-md",
        lg: "rounded-lg",
        xl: "rounded-xl",
        square: "rounded-none",
        full: "rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
      rounded: "md",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      rounded,
      asChild = false,
      isLoading,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, rounded, className }))}
        ref={ref}
        disabled={isLoading}
        {...props}
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {children}
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
